import React, { useState, useEffect } from 'react';
import { Dialog, Box, Typography, TextField, Button } from '@mui/material';
import { transactionAPI, dashboardAPI } from '../../services/api';
import { formatAmountForDisplay } from '../../utils/amountFormatter';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import CopyButton from '../../components/ui/CopyButton';

const ConfirmVersementModal = ({ open, onClose, transaction, onConfirmed }) => {
  const [montantConstate, setMontantConstate] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentDirection, setAdjustmentDirection] = useState(null);
  const [note, setNote] = useState('');
  const [caisseBalance, setCaisseBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (open && transaction) {
      setMontantConstate('');
      setAdjustmentAmount('');
      setAdjustmentDirection(null);
      setNote('');
      setCaisseBalance(null);
      dashboardAPI.getCaisseDashboard(transaction.caisse.id)
        .then(res => setCaisseBalance(res.data.caisseBalance ?? null))
        .catch(() => {});
    }
  }, [open, transaction]);

  useEffect(() => {
    const DEFAULT = 'Ajustement - Confirmation de versement';
    if (adjustmentAmount && !note) {
      setNote(DEFAULT);
    } else if (!adjustmentAmount && note === DEFAULT) {
      setNote('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustmentAmount]);

  useEffect(() => {
    const declared = Number(transaction?.amount) || 0;
    const constate = montantConstate !== '' ? Number(montantConstate) : null;
    if (constate === null) {
      setAdjustmentAmount('');
      setAdjustmentDirection(null);
      return;
    }
    const ecart = parseFloat((constate - declared).toFixed(2));
    if (ecart > 0) {
      setAdjustmentAmount(String(ecart));
      setAdjustmentDirection('DEDUCT');
    } else if (ecart < 0) {
      setAdjustmentAmount(String(Math.abs(ecart)));
      setAdjustmentDirection('ADD');
    } else {
      setAdjustmentAmount('');
      setAdjustmentDirection(null);
    }
  }, [montantConstate, transaction]);

  if (!transaction) return null;

  const versementAmount = Number(transaction.amount) || 0;
  const constate = montantConstate !== '' ? Number(montantConstate) : null;
  const ecart = constate !== null ? constate - versementAmount : null;
  const adjAmt = adjustmentAmount !== '' ? Number(adjustmentAmount) : 0;
  const nouveauSolde = caisseBalance !== null
    ? caisseBalance + (adjustmentDirection === 'ADD' ? adjAmt : adjustmentDirection === 'DEDUCT' ? -adjAmt : 0)
    : null;

  const ecartColor = (val) => {
    if (val === null) return '#000000';
    if (val > 0) return '#0AD69F';
    if (val < 0) return '#EF456E';
    return '#000000';
  };

  const formatSigned = (val) => val !== null ? formatAmountForDisplay(val, true).text : '—';

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const pad = (n) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} - ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      height: '48px',
      borderRadius: '12px',
      background: '#FFFFFF',
      fontFamily: 'Montserrat',
      fontSize: '15px',
      fontWeight: 600,
      color: '#38485C',
      '& fieldset': { border: '1px solid #E3E3E3' },
      '&:hover fieldset': { border: '1px solid #027BC6' },
      '&.Mui-focused fieldset': { border: '1px solid #027BC6' },
    },
    '& .MuiInputBase-input': {
      padding: '0 14px !important',
    },
    '& .MuiInputBase-input::placeholder': { color: '#A3A3A3', opacity: 1 },
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await transactionAPI.confirmVersement(transaction.id, {
        montantConstate: Number(montantConstate),
        montantAjustement: adjustmentAmount !== '' ? Number(adjustmentAmount) : null,
        ajustementDirection: adjustmentDirection,
        ajustementNote: note || null,
      });
      setShowConfirmDialog(false);
      onClose();
      onConfirmed();
    } catch (e) {
      console.error('Error confirming versement:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1600 }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: '#F9FAFC',
            boxShadow: '0px 20px 80px #00000011',
            p: '28px',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '22px', color: '#38485C', mb: '16px' }}>
          Confirmer le montant
        </Typography>

        {/* Transaction recap */}
        <Box sx={{ background: '#E3E3E33C', borderRadius: '12px', border: '1px solid #E3E3E3', p: '14px 16px', mb: '20px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '2px' }}>
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 500, color: '#445E76', opacity: 0.88 }}>
              {transaction.code}
            </Typography>
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 700, color: '#EF456E' }}>
              {formatAmountForDisplay(-versementAmount, true).text}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography sx={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, color: '#027BC6', mb: '2px', mt: '-6px' }}>
                {transaction.category?.label || 'Versement Coffre'}
              </Typography>
              <Typography sx={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                {transaction.caisse?.name} · {transaction.createdBy?.firstName} {transaction.createdBy?.lastName}
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#9A9A9A', flexShrink: 0, ml: '12px' }}>
              {formatDate(transaction.createdAt)}
            </Typography>
          </Box>
          {transaction.description && (
            <Box sx={{ mt: '10px' }}>
              <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 500, color: '#445E76', opacity: 0.88 }}>
                Description :
              </Typography>
              <Typography sx={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', lineHeight: '20px', wordBreak: 'break-word' }}>
                {transaction.description}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Montant Attendu + Montant Reçu */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '48px', gap: '12px', mb: '12px' }}>
          <Box sx={{
            background: '#E9F5FD', borderRadius: '12px',
            height: '48px', px: '14px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography sx={{ fontFamily: 'Montserrat', fontSize: '9px', fontWeight: 600, color: '#027BC6', textTransform: 'uppercase', letterSpacing: '0.5px', mb: '2px' }}>
                Montant attendu
              </Typography>
              <Typography sx={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, color: '#1A2B3C', lineHeight: 1 }}>
                {formatAmountForDisplay(versementAmount, false).text}
              </Typography>
            </Box>
            <CopyButton value={Number.isInteger(versementAmount) ? String(versementAmount) : versementAmount.toFixed(2)} size={14} />
          </Box>
          <TextField
            type="text"
            inputMode="numeric"
            value={montantConstate}
            onChange={e => {
              let val = e.target.value.replace(/[^0-9.]/g, '');
              const dot = val.indexOf('.');
              if (dot !== -1) val = val.slice(0, dot + 1) + val.slice(dot + 1).replace(/\./g, '');
              setMontantConstate(val);
            }}
            placeholder="Montant Reçu"
            sx={inputSx}
          />
        </Box>

        {/* Écart */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#E9F5FD', borderRadius: '12px', minHeight: '48px', flexShrink: 0, px: '14px', mb: '20px',
        }}>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 500, color: '#000000', opacity: 0.69 }}>
            Écart
          </Typography>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '15px', fontWeight: 600, color: ecartColor(ecart), opacity: 0.88 }}>
            {ecart !== null ? formatSigned(ecart) : '—'}
          </Typography>
        </Box>

        {/* Ajustement du solde */}
        <Box sx={{ mb: '20px' }}>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, color: '#000000', mb: '10px' }}>
            Ajustement du solde
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', mb: '12px' }}>
            <TextField
              type="text"
              inputMode="decimal"
              value={adjustmentAmount}
              onChange={e => setAdjustmentAmount(e.target.value)}
              placeholder="Montant ajustement"
              sx={inputSx}
            />
            <Box sx={{
              display: 'flex', background: '#E3E3E33C', border: '1px solid #E3E3E3',
              borderRadius: '12px', height: '48px', padding: '4px', gap: '3px',
            }}>
              <Box
                onClick={() => setAdjustmentDirection(d => d === 'ADD' ? null : 'ADD')}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  borderRadius: '9px', userSelect: 'none', transition: 'all 0.15s',
                  background: adjustmentDirection === 'ADD' ? '#FFFFFF' : 'transparent',
                  border: adjustmentDirection === 'ADD' ? '1px solid #027BC6' : 'none',
                  color: adjustmentDirection === 'ADD' ? '#027BC6' : '#75808E',
                }}
              >
                + Ajouter
              </Box>
              <Box
                onClick={() => setAdjustmentDirection(d => d === 'DEDUCT' ? null : 'DEDUCT')}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  borderRadius: '9px', userSelect: 'none', transition: 'all 0.15s',
                  background: adjustmentDirection === 'DEDUCT' ? '#FFFFFF' : 'transparent',
                  border: adjustmentDirection === 'DEDUCT' ? '1px solid #EF456E' : 'none',
                  color: adjustmentDirection === 'DEDUCT' ? '#EF456E' : '#75808E',
                }}
              >
                - Déduire
              </Box>
            </Box>
          </Box>
          <Box sx={{
            borderRadius: '12px', border: '1px solid #E3E3E3', background: '#FFFFFF', padding: '12px',
            '& textarea::placeholder': { color: '#AFB0B2', opacity: 1, fontFamily: 'Montserrat', fontSize: '15.5px', fontWeight: 600 },
          }}>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Note"
              style={{
                width: '100%', height: '54px', border: 'none', outline: 'none',
                fontFamily: 'Montserrat', fontSize: '15.5px', fontWeight: 600, color: '#38485C',
                backgroundColor: 'transparent', resize: 'none', overflowY: 'auto',
                boxSizing: 'border-box', display: 'block',
              }}
            />
          </Box>
        </Box>

        {/* Solde preview */}
        <Box sx={{ display: 'flex', background: '#E9F5FD', borderRadius: '12px', p: '24px 28px', gap: '16px', mb: '16px', alignItems: 'stretch' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 500, color: '#000000', opacity: 0.88, mb: '2px', textTransform: 'capitalize' }}>
              Solde caisse précédent
            </Typography>
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '26px', fontWeight: 700, color: '#027BC6', opacity: 0.88, lineHeight: 1.1 }}>
              {caisseBalance !== null ? formatAmountForDisplay(caisseBalance, false).text : '—'}
            </Typography>
          </Box>
          <Box sx={{ width: '1px', background: '#C0DCF0', my: '-6px' }} />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', pl: '12px' }}>
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 500, color: '#000000', opacity: 0.88, mb: '2px', textTransform: 'capitalize' }}>
              Nouveau solde caisse
            </Typography>
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '26px', fontWeight: 700, color: '#027BC6', opacity: 0.88, lineHeight: 1.1 }}>
              {nouveauSolde !== null
                ? formatAmountForDisplay(nouveauSolde, false).text
                : caisseBalance !== null
                  ? formatAmountForDisplay(caisseBalance, false).text
                  : '—'}
            </Typography>
          </Box>
        </Box>

        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '24px' }}>
          <Button
            onClick={onClose}
            sx={{
              height: '44px', borderRadius: '12px',
              fontFamily: 'Montserrat', fontWeight: 600, fontSize: '16px',
              color: '#36495C', textTransform: 'none', background: 'none', boxShadow: 'none',
              '&:hover': { background: 'rgba(54,73,92,0.04)' }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={() => setShowConfirmDialog(true)}
            disabled={!montantConstate}
            sx={{
              width: '140px', height: '44px', borderRadius: '12px',
              fontFamily: 'Montserrat', fontWeight: 600, fontSize: '16px',
              color: '#FFFFFF', textTransform: 'none', boxShadow: 'none',
              background: montantConstate ? '#027BC6' : '#CCCCCC',
              '&:hover': { background: montantConstate ? '#025a9c' : '#CCCCCC' },
              '&:disabled': { background: '#CCCCCC', color: '#FFFFFF' },
            }}
          >
            Valider
          </Button>
        </Box>
      </Dialog>

      <ConfirmationDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirm}
        type="rapprochement"
        loading={loading}
      />
    </>
  );
};

export default ConfirmVersementModal;
